import React, { useCallback, useEffect, useRef } from "react"
import Button from "../../../generic/buttons/Button/Button"
import useLockBodyScroll from "../../../../hooks/useLockBodyScroll"
import greenIcon from "../../../../assets/icons/big-icon-check-green.svg"
import redIcon from "../../../../assets/icons/big-icon-check-red.svg"
import classes from "./Modal.module.scss"

interface IModalProps {
  children?: string | JSX.Element | JSX.Element[]
  title?: string
  close: () => void
  headerIcon?: boolean
  headerIconColor?: "green" | "red"
  dialogSizeSuffix?: "w320" | "w350" | "w435" | "w620"
  headerAlignCenter?: boolean
  submitClick?: () => void
  submitText?: string
  submitLoading?: boolean
  submitDisabled?: boolean
  cancelClick?: () => void
  enableEnterNavigation?: boolean
}

function Modal({
    title,
    children,
    close,
    headerIcon = false,
    headerIconColor,
    dialogSizeSuffix = "w620",
    headerAlignCenter,
    submitClick,
    submitText = "Сохранить",
    submitLoading,
    submitDisabled,
    cancelClick,
    enableEnterNavigation = true
  }: IModalProps) {

  useLockBodyScroll()
  const dialogRef = useRef<HTMLDivElement>(null)

const handleKeyDown = useCallback((e: KeyboardEvent) => {
  const isTextarea = e.target instanceof HTMLTextAreaElement
  if (isTextarea && e.shiftKey && e.key === 'Enter') {
    return
  }

  if (e.key === 'Enter' && !e.shiftKey && enableEnterNavigation) {
    e.preventDefault()
    
    if (!dialogRef.current) return
    
    const allElements = Array.from(
      dialogRef.current.querySelectorAll(
        'input:not([disabled]):not([type="hidden"]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]), ' +
        'button:not([disabled]), ' +
        '[tabindex]:not([tabindex="-1"])'
      )
    ) as HTMLElement[]
    
    const focusableElements = allElements.filter(element => {
      const style = window.getComputedStyle(element)
      return style.display !== 'none' && 
             style.visibility !== 'hidden' && 
             !element.hasAttribute('disabled') &&
             element.offsetParent !== null
    })
    
    if (focusableElements.length === 0) return
    
    const activeElement = document.activeElement as HTMLElement
    const currentIndex = focusableElements.indexOf(activeElement)
    
    if (currentIndex !== -1) {
      let nextIndex = currentIndex + 1
    
      if (nextIndex >= focusableElements.length) {
        nextIndex = 0
      }
      
      const nextElement = focusableElements[nextIndex]

      if (nextElement && nextElement !== activeElement) {
        nextElement.focus()

        if (nextElement.tagName === 'SELECT') {
          setTimeout(() => {
            nextElement.click()
          }, 10)
        }
      }
    } else {
      focusableElements[0]?.focus()
    }
  }
}, [enableEnterNavigation])

  useEffect(() => {
    if (enableEnterNavigation) {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [enableEnterNavigation, handleKeyDown])

  const clickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    const targetEl = e.target as HTMLElement
    const elClasses = targetEl.className.split(' ')

    if (elClasses.includes(classes.overlay) || elClasses.includes(classes.close)) {
      close()
    }
  }

  const closeClickHandler = () => {
    close()
  }

  const dialogSizeClass = dialogSizeSuffix ? `dialog_${dialogSizeSuffix}` : ``

  return (
    <div
      className={classes.overlay}
      onMouseDown={clickHandler}
    >
      <div
        ref={dialogRef}
        className={[classes.dialog, classes[dialogSizeClass]].join(" ")}
      >
        <div className={classes.content}>
          <button
            type={"button"}
            className={classes.close}
          ></button>
          <div className={[classes.header, headerAlignCenter && classes.header_center].join(" ")}>
            {headerIcon && <div className={classes.icon}>
              <img src={headerIconColor === "red" ? redIcon : greenIcon} alt=""/>
            </div>}
            {title && <h1 className={classes.title}>{title}</h1>}
          </div>
          <div className={classes.body}>
            {children && <div className={classes.body_content}>{children}</div>}
          </div>
          {(submitClick || cancelClick) &&
            <div className={classes.footer}>
              {submitClick &&
                <Button
                  color={"primary"}
                  onClick={submitClick}
                  type={"button"}
                  disabled={submitDisabled}
                  isLoading={submitLoading}
                >
                  {submitText}
                </Button>
              }
              {cancelClick &&
                <Button
                  color={"white"}
                  onClick={closeClickHandler}
                >Отмена</Button>
              }
            </div>
          }
        </div>
      </div>
    </div>
  )
}

export default Modal